import { useEffect, useRef, useCallback } from 'react'
import { logger } from '@/lib/utils/logger'

/**
 * Custom hook to manage the Screen Wake Lock API.
 * Prevents the screen from dimming or locking during active voice calls.
 * 
 * This implementation includes:
 * - Native Screen Wake Lock API (preferred)
 * - Silent audio fallback for iOS Safari when wake lock fails
 * - Manual trigger function to be called during user gestures (required for iOS)
 * - Auto re-acquisition when page regains visibility
 */
export function useWakeLock(isActive: boolean) {
    const wakeLockRef = useRef<WakeLockSentinel | null>(null)
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const usingAudioFallback = useRef(false)

    // Silent audio fallback for iOS - plays a very quiet audio loop
    // This tricks iOS into thinking media is playing, keeping the screen on
    const startAudioFallback = useCallback(() => {
        if (audioRef.current) return // Already running

        try {
            // Create a silent audio element with a very short silent audio data URL
            // This is a 0.1 second silent MP3 that loops
            const silentAudioBase64 = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIgAANP8AAABM/AAAAABNXQAAAAAADV0AAAAAAAA1dAAAAAAAAGsAAAAAAAAAbgAAAAAAAABuAAAAAAAAAG4AAAAAAAAALQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP/7UMQDgAjAAaf4AAAIAAADSIAAAE38AAAAAExcAAAAABq6AAAAAAAANXQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAbQAAAAAAAGsAAAAAAAAAawAAAAAAAABrAAAAAAAAAGv///////////////////////////////////////////////////////////////////////////////////////////////////////////////8='

            const audio = new Audio(silentAudioBase64)
            audio.loop = true
            audio.volume = 0.01 // Very quiet, essentially silent
            audio.setAttribute('playsinline', 'true') // Required for iOS

            // Play the audio - this helps keep iOS awake
            audio.play().then(() => {
                audioRef.current = audio
                usingAudioFallback.current = true
                if (import.meta.env.DEV) {
                    console.log('[useWakeLock] Audio fallback started for iOS')
                }
                logger.info('[useWakeLock] Audio fallback started for iOS')
            }).catch(err => {
                // Audio play can fail without user gesture
                if (import.meta.env.DEV) {
                    console.warn('[useWakeLock] Audio fallback failed:', err)
                }
            })
        } catch (err) {
            logger.warn('[useWakeLock] Failed to create audio fallback', err)
        }
    }, [])

    const stopAudioFallback = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.src = ''
            audioRef.current = null
            usingAudioFallback.current = false
            if (import.meta.env.DEV) {
                console.log('[useWakeLock] Audio fallback stopped')
            }
        }
    }, [])

    const requestWakeLock = useCallback(async () => {
        // Check if Wake Lock API is supported
        if (!('wakeLock' in navigator)) {
            logger.warn('[useWakeLock] Wake Lock API not supported, trying audio fallback')
            startAudioFallback()
            return
        }

        try {
            wakeLockRef.current = await navigator.wakeLock.request('screen')

            if (import.meta.env.DEV) {
                console.log('[useWakeLock] Wake lock acquired')
            }
            logger.info('[useWakeLock] Wake lock acquired - screen will stay on')

            // Stop audio fallback if wake lock succeeded
            stopAudioFallback()

            // Handle wake lock release (e.g., when tab/window loses visibility)
            wakeLockRef.current.addEventListener('release', () => {
                if (import.meta.env.DEV) {
                    console.log('[useWakeLock] Wake lock released by system')
                }
                logger.info('[useWakeLock] Wake lock released')
                wakeLockRef.current = null
            })
        } catch (err) {
            // Wake lock request can fail for various reasons:
            // - Page not visible
            // - Low battery mode
            // - Browser restrictions
            // - iOS: not called during user gesture
            logger.warn('[useWakeLock] Failed to acquire wake lock, falling back to audio', err)
            if (import.meta.env.DEV) {
                console.warn('[useWakeLock] Failed to acquire wake lock, trying audio fallback:', err)
            }
            // Fall back to audio method
            startAudioFallback()
        }
    }, [startAudioFallback, stopAudioFallback])

    const releaseWakeLock = useCallback(async () => {
        // Release native wake lock
        if (wakeLockRef.current) {
            try {
                await wakeLockRef.current.release()
                wakeLockRef.current = null

                if (import.meta.env.DEV) {
                    console.log('[useWakeLock] Wake lock manually released')
                }
                logger.info('[useWakeLock] Wake lock manually released')
            } catch (err) {
                logger.warn('[useWakeLock] Error releasing wake lock', err)
            }
        }

        // Also stop audio fallback
        stopAudioFallback()
    }, [stopAudioFallback])

    // Acquire or release wake lock based on active state
    useEffect(() => {
        if (isActive) {
            requestWakeLock()
        } else {
            releaseWakeLock()
        }

        // Cleanup on unmount
        return () => {
            releaseWakeLock()
        }
    }, [isActive, requestWakeLock, releaseWakeLock])

    // Re-acquire wake lock when page becomes visible again
    // (Wake lock is automatically released when page loses visibility)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isActive) {
                if (!wakeLockRef.current && !usingAudioFallback.current) {
                    if (import.meta.env.DEV) {
                        console.log('[useWakeLock] Page visible again, re-acquiring wake lock')
                    }
                    requestWakeLock()
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [isActive, requestWakeLock])

    return {
        isSupported: 'wakeLock' in navigator,
        isActive: !!wakeLockRef.current || usingAudioFallback.current,
        // Manual trigger that can be called during user gesture (e.g., button click)
        // This is important for iOS which may require user gesture for wake lock
        requestWakeLockManually: requestWakeLock
    }
}

